import { useEffect, useState } from "react";
import { Form, Input, Button, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import get from "lodash/get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faFilm,
  faTrashAlt,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";

type CollabSectionProps = {
  type: "instagram-reels" | "instagram-story" | "tiktok-video";
  namePrefix: (string | number)[];
  placeholder?: string;
};

const labelByType: Record<string, React.ReactNode> = {
  "instagram-reels": (
    <span>
      <FontAwesomeIcon
        icon={faVideo}
        style={{ color: "#ff9f1c", marginRight: 8 }}
      />{" "}
      Instagram Reel
    </span>
  ),
  "instagram-story": (
    <span>
      <FontAwesomeIcon
        icon={faFilm}
        style={{ color: "#ff9f1c", marginRight: 8 }}
      />{" "}
      Instagram Story
    </span>
  ),
  "tiktok-video": (
    <span>
      <FontAwesomeIcon
        icon={faVideo}
        style={{ color: "#ff9f1c", marginRight: 8 }}
      />{" "}
      TikTok Video
    </span>
  ),
};

const OptionsByType: Record<string, string[]> = {
  "instagram-reels": [
    "Tag as a collaborator",
    "Copy and adapt this description",
    "Copy and adapt this 1st comment",
  ],
  "instagram-story": [
    "Add mention",
    "Add link",
    "Copy and adapt this text",
    "Highlight your Reel and include the link to it",
  ],
  "tiktok-video": [
    "Mention in the description",
    "Copy and adapt this description",
    "Copy and adapt this 1st comment",
  ],
};

const textAreaOptions = [
  "Copy and adapt this description",
  "Copy and adapt this text",
  "Copy and adapt this 1st comment",
  "Highlight your Reel and include the link to it",
];

const CollabSection: React.FC<CollabSectionProps> = ({ type, namePrefix }) => {
  const form = Form.useFormInstance();

  const [enabled, setEnabled] = useState<boolean>(false);

  useEffect(() => {
    const values = form.getFieldValue(namePrefix);
    if (Array.isArray(values) && values.length > 0) {
      setEnabled(true);
    } else {
      setEnabled(false);
    }
  }, [form, namePrefix]);

  if (!enabled) return null;

  return (
    <div style={{ marginBottom: 0 }}>
      {/* <Checkbox
        checked={enabled}
        onChange={(e) => {
          const newEnabled = e.target.checked;
          setEnabled(newEnabled);
          if (newEnabled) {
            const values = form.getFieldValue(namePrefix);
            if (!values || values.length === 0) {
              form.setFieldValue(namePrefix, [
                {
                  actionType: undefined,
                  copyText: "",
                  extraAction: undefined,
                  extraInstructions: "",
                },
              ]);
            }
          }
        }}
        style={{ marginBottom: 12, fontSize: 18 }}
      >
       
      </Checkbox> */}
      <span
        style={{
          fontSize: 20,
          display: "block",
          marginBottom: 12,
        }}
      >
        {labelByType[type]}{" "}
      </span>
      {enabled && (
        <Form.List name={namePrefix}>
          {(fields, { add, remove }) => (
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fdf9f3",
                padding: 32,
                borderRadius: 12,
              }}
            >
              {fields.map(({ key, name, ...restField }, index) => (
                <div
                  key={key}
                  style={{
                    display: "flex",

                    position: "relative",
                    flex: 1,
                    flexDirection: "column",
                  }}
                >
                  <Form.Item
                    {...restField}
                    name={[name, "instruction"]}
                    style={{ width: "100%", marginBottom: 14 }}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Select
                      placeholder="Choose an action"
                      popupMatchSelectWidth={false}
                      options={OptionsByType[type].map((option) => ({
                        label: option,
                        value: option,
                      }))}
                    />
                  </Form.Item>

                  <div style={{ display: "flex", gap: 8, width: "100%" }}>
                    <Form.Item
                      shouldUpdate={(prevValues, curValues) => {
                        const prev = get(prevValues, [
                          ...namePrefix,
                          name,
                          "instruction",
                        ]);
                        const cur = get(curValues, [
                          ...namePrefix,
                          name,
                          "instruction",
                        ]);
                        return prev !== cur;
                      }}
                      style={{ width: "100%" }}
                    >
                      {() => {
                        const instruction = form.getFieldValue([
                          ...namePrefix,
                          name,
                          "instruction",
                        ]);
                        const isTextArea =
                          textAreaOptions.includes(instruction);

                        return (
                          <Form.Item
                            {...restField}
                            name={[name, "copyText"]}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ width: "100%", marginBottom: 0 }}
                          >
                            {isTextArea ? (
                              <Input.TextArea
                                placeholder="Enter text to copy"
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                style={{
                                  color: "#ADAEBC",
                                  position: "relative",
                                }}
                              />
                            ) : (
                              <Input
                                placeholder="@brand.name"
                                style={{ width: "100%", color: "#666" }}
                                suffix={
                                  <FontAwesomeIcon
                                    icon={faCopy}
                                    style={{ fontSize: 18 }}
                                    className="gray-icon-hover-black"
                                  />
                                }
                              />
                            )}
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      style={{
                        fontSize: 18,
                        marginTop: 6,
                        display: "block",
                        padding: 10,
                      }}
                      className="gray-icon"
                      onClick={() => remove(name)}
                    />
                  </div>
                  {fields.length - 1 === index && (
                    <Button
                      icon={<PlusOutlined />}
                      className="dashed-button"
                      onClick={() =>
                        add({
                          actionType: undefined,
                          copyText: "",
                          extraAction: undefined,
                          extraInstructions: "",
                        })
                      }
                      style={{
                        borderStyle: "dashed",
                        fontWeight: 400,
                        fontSize: 18,
                        height: 60,
                        marginTop: 6,
                      }}
                    >
                      Add another instruction
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Form.List>
      )}
    </div>
  );
};

export default CollabSection;
